import { useState, useEffect } from "react";
import "./styles/pay.scss";
import CreditCard from "./component/creditcard.js";
import axios from "axios";
import { backend } from "../../components/apipath";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { usePayment } from "../../components/PaymentContext";

function Pay() {
  const { step } = usePayment();
  const [errors, setErrors] = useState({});
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [formData, setFormData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("ATM");
  const [ccInputs, setCcInputs] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 這個冪等鍵本頁面只產一次，帶到後端做去重
  const [idemKey] = useState(
    () =>
      (window.crypto?.randomUUID?.() ??
        `ord_${Date.now()}_${Math.random().toString(36).slice(2)}`)
  );

  const navigate = useNavigate();

  // 常數
  const BOOK_UNIT_PRICE = 2500;

  // Step gate
  useEffect(() => {
    const savedStep = parseInt(localStorage.getItem("paymentStep")) || 0;
    if (step < 3 && savedStep < 3) {
      navigate("/registration/payinfo");
    }
  }, [step, navigate]);

  // 載入方案 & 前一頁表單
  useEffect(() => {
    (async () => {
      try {
        const selectedPlan = localStorage.getItem("selectedPlan"); // 產品 id
        const res = await axios.get(`${backend}product/${selectedPlan}`);
        setPlanData(res.data.data[0]);
      } catch (err) {
        console.error("Error fetching plan details:", err);
      }
      const stored = JSON.parse(localStorage.getItem("formData") || "{}");
      setFormData(stored);
    })();
  }, []);

  const handlePaymentMethodChange = (method) => setPaymentMethod(method);

  // 優惠碼
  const checkCoupon = async () => {
    if (isSubmitting) return; // 送單時禁止再驗證
    try {
      const res = await fetch(`${backend}order/checkCoupon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: coupon }),
      });
      const data = await res.json();
      if (data.success) {
        setCouponInfo(data);
      } else {
        Swal.fire({
          icon: "error",
          title: "優惠碼錯誤",
          text: data.message || "查無此優惠碼，請重新確認！",
        });
      }
    } catch (e) {
      console.error(e);
      Swal.fire({
        icon: "error",
        title: "連線失敗",
        text: "無法驗證優惠碼，請稍後再試。",
      });
    }
  };

  // 折扣邏輯：只折「方案＋書本」，不折運費
  const calculateDiscountedPrice = (price) => {
    if (!couponInfo) return price;
    const discount = parseFloat(couponInfo.data.discount || 0);
    return Math.max(0, Number(price) - discount);
  };

  // ======= 計算顯示金額（關鍵邏輯）=======
  const isCSU = String(planData?.product_id ?? "") === "10";
  const parentId = String(planData?.parent_id ?? "");
  const isCPA = parentId === "1";
  const isCMA = parentId === "2";
  const isEA  = parentId === "3";
  const isUT  = parentId === "6";
  const hasBookOption = isCPA || isCMA || isEA;

  const unitPrice = Number(planData?.price || 0);
  const csuCount = isCSU
    ? (Array.isArray(formData?.bridge_courses)
        ? formData.bridge_courses.length
        : 0)
    : 1;

  // 方案本體
  const planSubtotal = unitPrice * csuCount;

  // 書本（CPA/EA/CMA 都計）
  const bookCount =
  hasBookOption && Array.isArray(formData?.book) ? formData.book.length : 0;
  const booksTotal = bookCount * BOOK_UNIT_PRICE;
  // 運費（僅 CPA + 勾「快遞」 + 有書）
  const needsCourier =
    isCPA &&
    Array.isArray(formData?.deliver) &&
    formData.deliver.includes("快遞");
  const shippingFee =
    needsCourier && bookCount > 0
      ? Math.ceil(Math.min(bookCount, 4) / 2) * 100
      : 0;

  // 未折扣小計（含運費）
  const baseSubtotal = planSubtotal + booksTotal + shippingFee;

  // 折扣後（方案+書）＋ 運費
  const discountedPlanPlusBooks = calculateDiscountedPrice(
    planSubtotal + booksTotal
  );
  const discountedTotal = discountedPlanPlusBooks + shippingFee;

  // 送單
  const handleSubmit = async () => {
    if (isSubmitting) return; // 🚫 二次點擊直接忽略
    setIsSubmitting(true);

    try {
      const form_data = JSON.parse(localStorage.getItem("formData") || "{}");
      const mem_data = JSON.parse(localStorage.getItem("auth") || "{}");
      const transfer_date = document.getElementById("payment")?.value || "";
      const transfer_name = document.getElementById("payer")?.value || "";
      const order_date = new Date().toISOString().split("T")[0];

      let formValid = true;
      const newErrors = {};

      if (!transfer_date) {
        newErrors.transfer_date = "請填寫匯款 / 轉帳日期";
        formValid = false;
      }
      if (!transfer_name.trim()) {
        newErrors.transfer_name = "請填寫匯款人姓名或帳號末五碼";
        formValid = false;
      }
      setErrors(newErrors);
      if (!formValid) return;

      // 依產品 exp 月數算 software_ex
      const expMonths = planData?.exp ? parseInt(planData.exp, 10) : 12;
      const software_ex = new Date();
      if (!isNaN(expMonths)) {
        software_ex.setMonth(software_ex.getMonth() + expMonths);
      }

      // 重新計算（以最新 form_data 為準）
      const _isCSU = String(planData?.product_id ?? "") === "10";
      const _parentId = String(planData?.parent_id ?? "");
      const _isCPA = _parentId === "1";
      const _isCMA = _parentId === "2";
      const _isEA  = _parentId === "3";
      const _isUT  = _parentId === "6";
      const _hasBookOption = _isCPA || _isCMA || _isEA;

      const _unitPrice = Number(planData?.price || 0);
      const _csuCount = _isCSU
        ? (Array.isArray(form_data.bridge_courses)
            ? form_data.bridge_courses.length
            : 0)
        : 1;

      const _planSubtotal = _unitPrice * _csuCount;

      const _bookCount =
        _hasBookOption && Array.isArray(form_data.book) ? form_data.book.length : 0;
      const _booksTotal = _bookCount * BOOK_UNIT_PRICE;

      const _needsCourier =
        _isCPA &&
        Array.isArray(form_data?.deliver) &&
        form_data.deliver.includes("快遞");
      const _shippingFee =
        _needsCourier && _bookCount > 0
          ? Math.ceil(Math.min(_bookCount, 4) / 2) * 100
          : 0;

      const _discountedPlanPlusBooks = calculateDiscountedPrice(
        _planSubtotal + _booksTotal
      );
      const finalPrice = _discountedPlanPlusBooks + _shippingFee;

      const coupon = couponInfo;

      const requestData = {
        mem_id: mem_data.mem_id,
        product_id: planData.product_id,
        total_price: finalPrice,

        discount_id:
          coupon?.type === "discount" ? coupon.data.discount_id : null,
        mou_id: coupon?.type === "mou" ? coupon.data.mou_id : null,
        partner_id: null,

        tax_num: form_data.tax_num || null,
        company: form_data.company || null,

        // CPE
        cpe_email: form_data.cpe_email || null,
        cpe_activation_date: form_data.cpe_activation_date || null,
        cpe_company_name: form_data.cpe_company_name || null,

        // CSU
        bridge_birthdate: _isCSU ? form_data.bridge_birthdate || null : null,
        bridge_courses: _isCSU ? form_data.bridge_courses || [] : null,

        // UT
        ut_activation_date: _isUT ? form_data.ut_activation_date || null : null,
        ut_reasons: _isUT
          ? (Array.isArray(form_data.ut_reasons)
              ? form_data.ut_reasons.join(",")
              : form_data.ut_reasons || "")
          : null,

        deliver: Array.isArray(form_data.deliver)
          ? form_data.deliver.join(",")
          : form_data.deliver || "",
        software_ex: !isNaN(software_ex.getTime())
          ? software_ex.toISOString().split("T")[0]
          : null,
        extension: null,
        memo: null,
        order_date,
        transfer_date,
        transfer_name,

        // CPA / EA / CMA 都可能有書：一律送
        book: Array.isArray(form_data.book) ? form_data.book.join(",") : "",
        course: Array.isArray(form_data.course)
          ? form_data.course.join(",")
          : "",
        address: form_data.address || null,
      };

      await axios.post(`${backend}order/create-order`, requestData, {
        headers: {
          "Content-Type": "application/json",
          // 👉 把冪等鍵一併送到後端
          "Idempotency-Key": idemKey,
        },
      });

      // 成功後不解鎖，直接導頁，避免使用者在彈窗期間再點
      Swal.fire({
        title: "購買成功",
        text: "感謝您購買成功！我們將於一個工作天內為您開立帳號並聯繫您。",
        icon: "success",
        confirmButtonText: "確定",
        timer: 3000,
        timerProgressBar: true,
        willClose: () => {
          window.location.href = "/";
        },
      });
    } catch (error) {
      console.error("Error creating order:", error?.response?.data || error?.message);
      Swal.fire({
        title: "購買失敗",
        text: "無法完成您的購買，請稍後再試。",
        icon: "error",
        confirmButtonText: "確定",
      });
    } finally {
      // 若失敗才解鎖，成功會導頁就不解鎖
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="container mb100 mt100">
        <div className="beckertitlename mt100 mb-5">付款資訊</div>

        {/* 送單期間整卡禁止操作：pe-none + 降低透明度 */}
        <div className={`card w-75 p-5 m-auto ${isSubmitting ? "pe-none opacity-50" : ""}`}>
          <div className="form-group mb-4">
            <div>
              {planData ? (
                <div className="plan-details mb-5">
                  <div className="mt-3">
                    <h4>購買方案</h4>
                    {planData.name}
                  </div>

                  <div className="mt-3">
                    <h4>方案內容</h4>
                    <div
                      className="card-body"
                      dangerouslySetInnerHTML={{ __html: planData.content }}
                    ></div>
                  </div>

                  {/* 方案本體金額（CSU 乘課程數） */}
                  <div className="mt-3">
                    <h4>方案價格</h4>
                    <div className="fs-5">$ {planSubtotal.toLocaleString()} 元</div>
                    {isCSU && (
                      <small className="text-muted d-block">
                        單門 ${unitPrice.toLocaleString()} × {csuCount} 門
                      </small>
                    )}
                  </div>

                  {/* 只要有書（CPA/EA/CMA）就顯示書本加購金額 */}
                  {bookCount > 0 && (
                    <div className="mt-2">
                      <h5 className="mb-1">書本加購</h5>
                      <div className="fs-6">
                        $ {BOOK_UNIT_PRICE.toLocaleString()} × {bookCount} 本
                        <strong className="ms-2">
                          = $ {booksTotal.toLocaleString()} 元
                        </strong>
                      </div>
                    </div>
                  )}

                  {/* CPA + 快遞 + 有書 才顯示運費 */}
                  {isCPA && needsCourier && bookCount > 0 && (
                    <div className="mt-2">
                      <h5 className="mb-1">快遞運費</h5>
                      <div className="fs-6">
                        {bookCount <= 2 ? "1 箱 × $100" : "2 箱 × $100"}
                        <strong className="ms-2">
                          = $ {shippingFee.toLocaleString()} 元
                        </strong>
                      </div>
                    </div>
                  )}

                  {/* 小計（未折扣，含運費） */}
                  <div className="mt-2">
                    <h5 className="mb-1">小計</h5>
                    <div className="fs-6">
                      <strong>$ {baseSubtotal.toLocaleString()} 元</strong>
                    </div>
                  </div>

                  {/* 折扣後金額（折方案+書，不折運費） */}
                  <div className="mt-3">
                    <h4>折扣後金額</h4>
                    <strong>$ {discountedTotal.toLocaleString()} 元</strong>
                  </div>

                  {/* 優惠碼 */}
                  <div className="mt-4">
                    <label>優惠碼</label>
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="請輸入優惠碼"
                        value={coupon}
                        onChange={(e) => setCoupon(e.target.value)}
                        style={{ maxWidth: "200px" }}
                        disabled={isSubmitting}
                      />
                      <button
                        className="btn btn-outline-primary"
                        onClick={checkCoupon}
                        disabled={isSubmitting}
                        type="button"
                      >
                        輸入
                      </button>
                    </div>
                    {couponInfo && (
                      <div className="text-success mt-2">
                        成功套用優惠碼：{couponInfo.data.code}，折抵 NT$
                        {couponInfo.data.discount}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p>加载中...</p>
              )}
            </div>

            <label className="mb-5">付款方式</label>
            <div className="middle">
              <label>
                <input
                  type="radio"
                  name="radio"
                  checked={paymentMethod === "ATM"}
                  onChange={() => handlePaymentMethodChange("ATM")}
                  disabled={isSubmitting}
                />
                <div className="front-end box">
                  <span>ATM轉帳</span>
                </div>
              </label>

              {/* 開信用卡就把這段打開，並把 isSubmitting 傳入 CreditCard 去鎖 UI */}
              {/*
              <label>
                <input
                  type="radio"
                  name="radio"
                  checked={paymentMethod === "CreditCard"}
                  onChange={() => handlePaymentMethodChange("CreditCard")}
                  disabled={isSubmitting}
                />
                <div className="back-end box">
                  <span>信用卡付款</span>
                </div>
              </label>
              */}
            </div>
          </div>

          {paymentMethod === "ATM" && (
            // 避免 Enter 觸發原生 submit
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="atm">
                <div className="card-body">
                  付費購買匯款資訊
                  <br />
                  【在台購買】
                  <br />
                  收款人戶名：捷進顧問有限公司
                  <br />
                  受款銀行：華南商業銀行信維分行 (代碼：008 )
                  <br />
                  收款人帳號：149-10-010062-0
                  <br />
                  <br />
                  匯款金額：以新台幣付費，依各方案價格 (若需快遞書籍，請加上運費)
                  <br />
                  快遞計費方式：單箱最多可裝 2 科書籍，每箱快遞費 TWD 100 元。
                  <br />
                  __________________
                  <br />
                  <br />
                  【海外購買】
                  <br />
                  Recipient Name: Direct Line Consulting Co.
                  <br />
                  Account Number: 149-10-010062-0
                  <br />
                  <br />
                  Bank: Hua Nan Commercial Bank Ltd.
                  <br />
                  Branch: Hsin-Wei Branch
                  <br />
                  SWIFT Code (Bank ID): <br />
                  <br />
                  HNBKTWTP149
                  <br />
                  Bank Address: 2F, No.6, Xinyi Rd. Sec. 4, Da'an Dist., 106
                  Taipei City, Taiwan
                  <br />
                  <br />
                  匯款金額：以新台幣付費，依各方案價格 (全額到匯)
                </div>

                <div className="form-group mb-4">
                  <label className="mb-2">匯款 / 轉帳日期</label>
                  <input
                    type="date"
                    className={`form-control ${errors.transfer_date ? "is-invalid" : ""}`}
                    id="payment"
                    disabled={isSubmitting}
                  />
                  <small className="form-text text-muted">
                    Becker Taiwan 將依此核對確認您的款項
                  </small>
                  {errors.transfer_date && (
                    <div className="invalid-feedback">{errors.transfer_date}</div>
                  )}
                </div>

                <div className="form-group mb-4">
                  <label className="mb-2">匯款 (存款) 人姓名、或轉出帳戶後五碼</label>
                  <input
                    type="text"
                    className={`form-control ${errors.transfer_name ? "is-invalid" : ""}`}
                    id="payer"
                    disabled={isSubmitting}
                  />
                  {errors.transfer_name && (
                    <div className="invalid-feedback">{errors.transfer_name}</div>
                  )}
                </div>

                <button
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  type="button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "送出中…" : "送出"}
                </button>
              </div>
            </form>
          )}

          {paymentMethod === "CreditCard" && (
            <CreditCard
              setInputs={setCcInputs}
              inputs={ccInputs}
              handleSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default Pay;
